/**
 * Preferences store for Idle Depths.
 *
 * Manages user preferences with local-first pattern:
 * - All changes saved to localStorage immediately
 * - Authenticated users additionally sync to server (debounced)
 */

import {
  type UserPreferences,
  type ThemePreference,
  DEFAULT_PREFERENCES,
} from '@idle-depths/shared';
import * as Sentry from '@sentry/react';
import { makeAutoObservable, reaction, runInAction } from 'mobx';

import type { RootStore } from './RootStore';

import { getVanillaClient } from '@/lib/trpc';
import { LocalStorageService } from '@/services/LocalStorageService';

/**
 * Preferences store.
 *
 * Manages user preferences with automatic persistence.
 */
export class PreferencesStore {
  /** Current preferences */
  preferences: UserPreferences = { ...DEFAULT_PREFERENCES };

  /** Whether preferences are being synced to server */
  syncing = false;

  /** Sync debounce timer */
  private syncTimer: number | null = null;

  /** Sync debounce delay (ms) */
  private readonly SYNC_DEBOUNCE = 2000;

  /** Reference to root store */
  private readonly rootStore: RootStore;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, {
      // Private properties that shouldn't be observed
      rootStore: false,
      SYNC_DEBOUNCE: false,
    } as Record<string, false>);

    // Load initial preferences from localStorage
    this.loadFromLocalStorage();

    // Auto-save to localStorage on any change
    reaction(
      () => JSON.stringify(this.preferences),
      () => {
        this.saveToLocalStorage();
        this.queueServerSync();
      }
    );
  }

  /**
   * Initialize preferences.
   *
   * If authenticated, loads from server and merges with local.
   */
  async initialize(): Promise<void> {
    if (!this.rootStore.authStore.shouldSyncToServer) {
      return;
    }

    try {
      const client = getVanillaClient();
      const serverPrefs = await client.user.getPreferences.query();

      // Server preferences take precedence for authenticated users
      runInAction(() => {
        this.preferences = {
          ...DEFAULT_PREFERENCES,
          ...serverPrefs,
        };
        this.saveToLocalStorage();
      });
    } catch (error) {
      // Keep local preferences on error
      Sentry.captureException(error, {
        tags: { context: 'preferences_init' },
      });
    }
  }

  /**
   * Load preferences from localStorage.
   */
  private loadFromLocalStorage(): void {
    this.preferences = LocalStorageService.getPreferences();
  }

  /**
   * Save preferences to localStorage.
   */
  private saveToLocalStorage(): void {
    LocalStorageService.setPreferences(this.preferences);
  }

  /**
   * Queue a sync to server (debounced).
   */
  private queueServerSync(): void {
    if (!this.rootStore.authStore.shouldSyncToServer) {
      return;
    }

    if (this.syncTimer) {
      clearTimeout(this.syncTimer);
    }

    this.syncTimer = window.setTimeout(() => {
      void this.syncToServer();
    }, this.SYNC_DEBOUNCE);
  }

  /**
   * Sync preferences to server.
   */
  private async syncToServer(): Promise<void> {
    if (!this.rootStore.authStore.shouldSyncToServer) {
      return;
    }

    runInAction(() => {
      this.syncing = true;
    });

    try {
      const client = getVanillaClient();
      await client.user.updatePreferences.mutate(this.preferences);
    } catch (error) {
      // Don't fail silently - log for debugging
      Sentry.captureException(error, {
        tags: { context: 'preferences_sync' },
      });
    } finally {
      runInAction(() => {
        this.syncing = false;
      });
    }
  }

  /**
   * Set a single preference.
   *
   * @param key - Preference key
   * @param value - New value
   */
  setPreference<K extends keyof UserPreferences>(key: K, value: UserPreferences[K]): void {
    this.preferences[key] = value;
  }

  /**
   * Set multiple preferences at once.
   *
   * @param preferences - Partial preferences to merge
   */
  setPreferences(preferences: Partial<UserPreferences>): void {
    this.preferences = {
      ...this.preferences,
      ...preferences,
    };
  }

  /**
   * Reset preferences to defaults.
   */
  resetToDefaults(): void {
    this.preferences = { ...DEFAULT_PREFERENCES };
  }

  // =========================================================================
  // Convenience Getters and Setters
  // =========================================================================

  // Audio

  get musicVolume(): number {
    return this.preferences.musicVolume;
  }

  setMusicVolume(volume: number): void {
    this.setPreference('musicVolume', Math.max(0, Math.min(100, volume)));
  }

  get soundEffectsVolume(): number {
    return this.preferences.soundEffectsVolume;
  }

  setSoundEffectsVolume(volume: number): void {
    this.setPreference('soundEffectsVolume', Math.max(0, Math.min(100, volume)));
  }

  get musicEnabled(): boolean {
    return this.preferences.musicEnabled;
  }

  setMusicEnabled(enabled: boolean): void {
    this.setPreference('musicEnabled', enabled);
  }

  get soundEnabled(): boolean {
    return this.preferences.soundEnabled;
  }

  setSoundEnabled(enabled: boolean): void {
    this.setPreference('soundEnabled', enabled);
  }

  // UI

  get theme(): ThemePreference {
    return this.preferences.theme;
  }

  setTheme(theme: ThemePreference): void {
    this.setPreference('theme', theme);
  }

  get showGuestBanner(): boolean {
    return this.preferences.showGuestBanner;
  }

  dismissGuestBanner(): void {
    this.setPreference('showGuestBanner', false);
  }

  // GDPR Consent

  get analyticsConsent(): boolean {
    return this.preferences.analyticsConsent;
  }

  setAnalyticsConsent(consent: boolean): void {
    this.setPreference('analyticsConsent', consent);
  }

  get marketingConsent(): boolean {
    return this.preferences.marketingConsent;
  }

  setMarketingConsent(consent: boolean): void {
    this.setPreference('marketingConsent', consent);
  }

  /**
   * Reset store to initial state.
   */
  reset(): void {
    if (this.syncTimer) {
      clearTimeout(this.syncTimer);
      this.syncTimer = null;
    }
    this.preferences = { ...DEFAULT_PREFERENCES };
    this.syncing = false;
  }
}
